import type React from "react";
import {
  Box,
  Typography,
  Avatar,
  AvatarGroup,
  Tooltip,
  Chip,
  Link,
} from "@mui/material";
import { OpenInNew } from "@mui/icons-material";
import { useSelector } from "react-redux";
import type { RootState } from "../../../../store/Index";
import type { CrewEntry } from "../../components/callsheet/types/callsheet";

interface AssignedCrewPanelProps {
  crewMembersData: CrewEntry[];
  streamId?: number;
}

const AssignedCrewPanel: React.FC<AssignedCrewPanelProps> = ({
  crewMembersData,
  streamId,
}) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);

  if (crewMembersData.length === 0) return null;

  const getCrewName = (member: CrewEntry) =>
    member.crew_member?.name ||
    `${member.crew_member?.user?.first_name ?? ""} ${member.crew_member?.user?.last_name ?? ""}`.trim() ||
    "Crew";

  const getCrewRole = (member: CrewEntry) =>
    member.roles?.[0]?.name || member.assignment?.[0]?.role || "Crew";

  const producer = crewMembersData.find((m) =>
    getCrewRole(m).toLowerCase().includes("producer"),
  );

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        borderRadius: "20px",
        p: 2.5,
        boxShadow: "none",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
        }}
      >
        <Typography variant="h6" fontWeight="bold" sx={{ color: "#000000" }}>
          Assigned Crew
        </Typography>
        <Chip
          label={`${crewMembersData.length} member${crewMembersData.length !== 1 ? "s" : ""}`}
          size="small"
          variant="outlined"
          sx={{ color: "#6c757d", borderColor: "#eff2f5" }}
        />
      </Box>

      <AvatarGroup
        max={6}
        sx={{
          justifyContent: "flex-start",
          mb: producer ? 2 : 0,
          "& .MuiAvatar-root": {
            width: 36,
            height: 36,
            fontSize: "0.9rem",
            border: "2px solid #f8f9fa",
          },
        }}
      >
        {crewMembersData.map((member) => {
          const name = getCrewName(member);
          const role = getCrewRole(member);
          const pic = member.crew_member?.user?.profile_picture || undefined;
          return (
            <Tooltip key={member.id} title={`${name} · ${role}`}>
              <Avatar
                src={pic}
                sx={{ bgcolor: reduxTheme.primary_colors?.[500] || "#6c757d" }}
              >
                {!pic && name.charAt(0).toUpperCase()}
              </Avatar>
            </Tooltip>
          );
        })}
      </AvatarGroup>

      {producer && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mt: 1.5,
            pt: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              src={producer.crew_member?.user?.profile_picture || undefined}
              sx={{
                width: 32,
                height: 32,
                bgcolor: reduxTheme.primary_colors?.[400] || "#c1c4c9",
                fontSize: "0.875rem",
              }}
            >
              {!producer.crew_member?.user?.profile_picture &&
                getCrewName(producer).charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography
                variant="body2"
                display="block"
                fontWeight="bold"
                sx={{ color: "#000000" }}
              >
                {getCrewName(producer)}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                display="block"
              >
                {getCrewRole(producer)}
              </Typography>
            </Box>
          </Box>

          {streamId && (
            <Link
              href={`/stream-management/view/${streamId}/callsheet`}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                fontSize: "0.875rem",
                color: "#614438",
                fontWeight: 500,
              }}
            >
              View Callsheet
              <OpenInNew sx={{ fontSize: "0.85rem", color: "#614438" }} />
            </Link>
          )}
        </Box>
      )}
    </Box>
  );
};

export default AssignedCrewPanel;
